const CHANNEL_NAME = 'intelligential_session'
const STORAGE_KEY = 'intelligential_last_activity'
const LAST_ACTIVITY_TS_KEY = 'intelligential_last_activity_ts'
const DEFAULT_INACTIVITY_MINUTES = 20
const WARNING_SECONDS = 60
const ACTIVITY_THROTTLE_MS = 1000

const MSG = {
  ACTIVITY: 'activity',
  LOGOUT: 'logout',
  SHOW_WARNING: 'show_warning',
  DISMISS_WARNING: 'dismiss_warning'
}

class InactivityManager {
  constructor () {
    this._inactivityMs = DEFAULT_INACTIVITY_MINUTES * 60 * 1000
    this._warningSeconds = WARNING_SECONDS
    this._inactivityTimer = null
    this._warningTimer = null
    this._channel = null
    this._listeners = []
    this._lastBroadcast = 0
    this._onShowWarning = null
    this._onDismissWarning = null
    this._onLogout = null
    this._active = false
    this._warningVisible = false
  }

  init ({ inactivityMinutes, onShowWarning, onDismissWarning, onLogout }) {
    const resolvedInactivityMinutes = inactivityMinutes === undefined
      ? DEFAULT_INACTIVITY_MINUTES
      : inactivityMinutes

    if (resolvedInactivityMinutes === null || resolvedInactivityMinutes <= 0) {
      this.destroy()
      return
    }

    if (this._active) this.destroy()

    this._inactivityMs = resolvedInactivityMinutes * 60 * 1000
    this._warningSeconds = WARNING_SECONDS
    this._onShowWarning = onShowWarning
    this._onDismissWarning = onDismissWarning
    this._onLogout = onLogout
    this._active = true

    this._initChannel()
    this._attachListeners()
    this._resetTimer()
  }

  get isActive () { return this._active }

  destroy () {
    this._active = false
    clearTimeout(this._inactivityTimer)
    clearTimeout(this._warningTimer)
    this._detachListeners()
    if (this._channel) {
      this._channel.close()
      this._channel = null
    }
  }

  continueSession () {
    this._broadcast(MSG.DISMISS_WARNING)
    this._resetTimer()
  }

  _initChannel () {
    try {
      this._channel = new BroadcastChannel(CHANNEL_NAME)
      this._channel.onmessage = (e) => this._handleChannelMessage(e.data)
    } catch (e) {
      this._channel = null
      window.addEventListener('storage', this._handleStorageEvent.bind(this))
    }
  }

  _handleChannelMessage (msg) {
    if (msg.type === MSG.ACTIVITY) {
      this._resetTimerSilent()
    } else if (msg.type === MSG.LOGOUT) {
      this._onLogout && this._onLogout()
    } else if (msg.type === MSG.SHOW_WARNING) {
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_TS_KEY) || 0)
      const elapsed = Date.now() - lastActivity
      if (elapsed >= this._inactivityMs) {
        this._onShowWarning && this._onShowWarning()
      }
    } else if (msg.type === MSG.DISMISS_WARNING) {
      this._resetTimerSilent()
      this._onDismissWarning && this._onDismissWarning()
    }
  }

  _handleStorageEvent (e) {
    if (e.key !== STORAGE_KEY) return
    this._resetTimerSilent()
  }

  _attachListeners () {
    const handler = this._handleActivity.bind(this)
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart']
    events.forEach(evt => window.addEventListener(evt, handler, { passive: true }))
    this._listeners = events.map(evt => ({ evt, handler }))
  }

  _detachListeners () {
    this._listeners.forEach(({ evt, handler }) => window.removeEventListener(evt, handler))
    this._listeners = []
  }

  _handleActivity () {
    if (!this._active || this._warningVisible) return
    const now = Date.now()
    if (now - this._lastBroadcast < ACTIVITY_THROTTLE_MS) return
    this._lastBroadcast = now
    localStorage.setItem(LAST_ACTIVITY_TS_KEY, now)
    this._broadcast(MSG.ACTIVITY)
    this._resetTimer()
  }

  _broadcast (type) {
    const msg = { type }
    if (type === MSG.ACTIVITY) {
      localStorage.setItem(LAST_ACTIVITY_TS_KEY, Date.now())
    }
    if (this._channel) {
      this._channel.postMessage(msg)
    } else {
      localStorage.setItem(STORAGE_KEY, `${type}_${Date.now()}`)
    }
  }

  _resetTimer () {
    clearTimeout(this._inactivityTimer)
    clearTimeout(this._warningTimer)
    if (this._warningVisible) {
      this._warningVisible = false
      this._onDismissWarning && this._onDismissWarning()
    }

    const warningDelay = this._inactivityMs

    this._inactivityTimer = setTimeout(() => {
      this._warningVisible = true
      this._onShowWarning && this._onShowWarning()
      this._broadcast(MSG.SHOW_WARNING)

      this._warningTimer = setTimeout(() => {
        this._broadcast(MSG.LOGOUT)
        this._onLogout && this._onLogout()
      }, this._warningSeconds * 1000)
    }, warningDelay > 0 ? warningDelay : 0)
  }

  _resetTimerSilent () {
    clearTimeout(this._inactivityTimer)
    clearTimeout(this._warningTimer)
    if (this._warningVisible) {
      this._warningVisible = false
      this._onDismissWarning && this._onDismissWarning()
    }

    const warningDelay = this._inactivityMs

    this._inactivityTimer = setTimeout(() => {
      this._warningVisible = true
      this._onShowWarning && this._onShowWarning()
      this._broadcast(MSG.SHOW_WARNING)

      this._warningTimer = setTimeout(() => {
        this._broadcast(MSG.LOGOUT)
        this._onLogout && this._onLogout()
      }, this._warningSeconds * 1000)
    }, warningDelay > 0 ? warningDelay : 0)
  }
}

export default new InactivityManager()
